import { Address, NETWORK } from "@scure/btc-signer";

import { MAX_BATCH_ITEMS } from "./batch";

export const MAX_DESTINATION_CSV_BYTES = 64 * 1024;

interface CsvRow {
  cells: string[];
  line: number;
}

const ADDRESS_HEADERS = new Set(["address", "addr", "recipient", "destination", "walletaddress"]);

function headerName(value: string): string {
  return value.trim().toLowerCase().replace(/[\s_-]+/g, "");
}

function parseCsvRows(raw: string): CsvRow[] {
  const value = raw.replace(/^\uFEFF/, "");
  const rows: CsvRow[] = [];
  let cells: string[] = [];
  let cell = "";
  let quoted = false;
  let line = 1;
  let rowLine = 1;

  const finishRow = () => {
    cells.push(cell);
    if (cells.some((candidate) => candidate.trim())) rows.push({ cells, line: rowLine });
    cells = [];
    cell = "";
  };

  for (let index = 0; index < value.length; index += 1) {
    const character = value[index];
    if (quoted) {
      if (character === '"') {
        if (value[index + 1] === '"') {
          cell += '"';
          index += 1;
        } else {
          quoted = false;
        }
      } else {
        cell += character;
        if (character === "\n") line += 1;
      }
      continue;
    }

    if (character === '"' && !cell) {
      quoted = true;
    } else if (character === ",") {
      cells.push(cell);
      cell = "";
    } else if (character === "\n") {
      finishRow();
      line += 1;
      rowLine = line;
    } else if (character !== "\r") {
      cell += character;
    }
  }

  if (quoted) throw new Error(`Line ${rowLine}: the CSV has an unclosed quote`);
  if (cell || cells.length) finishRow();
  return rows;
}

function taprootAddress(value: string, line: number): string {
  const address = value.trim();
  if (!address) throw new Error(`Line ${line}: the destination address is blank`);
  if (address !== address.toLowerCase() || !address.startsWith("bc1p")) {
    throw new Error(`Line ${line}: use a lowercase mainnet Taproot address beginning bc1p`);
  }
  try {
    const decoded = Address(NETWORK).decode(address);
    if (decoded.type !== "tr") throw new Error("not Taproot");
  } catch {
    throw new Error(`Line ${line}: the destination is not a valid mainnet Taproot address`);
  }
  return address;
}

export function parseDestinationCsv(raw: string, expectedCount?: number): string[] {
  if (new TextEncoder().encode(raw).length > MAX_DESTINATION_CSV_BYTES) {
    throw new Error("Destination CSV files must be 64 KB or smaller");
  }
  const rows = parseCsvRows(raw);
  if (!rows.length) throw new Error("The destination CSV is empty");

  const headerIndexes = rows[0]!.cells
    .map((cell, index) => ADDRESS_HEADERS.has(headerName(cell)) ? index : -1)
    .filter((index) => index >= 0);
  if (headerIndexes.length > 1) throw new Error(`Line ${rows[0]!.line}: use only one address column`);
  const hasHeader = headerIndexes.length === 1;
  const addressIndex = headerIndexes[0] ?? 0;
  const addressRows = hasHeader ? rows.slice(1) : rows;

  if (!hasHeader && rows.some((row) => row.cells.length !== 1)) {
    throw new Error("Multi-column CSV files need an address or addr column header");
  }
  if (!addressRows.length) throw new Error("The destination CSV has no addresses");
  if (addressRows.length > MAX_BATCH_ITEMS) {
    throw new Error(`A destination CSV can contain at most ${MAX_BATCH_ITEMS} addresses`);
  }

  const addresses = addressRows.map((row) => taprootAddress(row.cells[addressIndex] ?? "", row.line));
  if (expectedCount !== undefined && addresses.length !== expectedCount) {
    throw new Error(`CSV has ${addresses.length} ${addresses.length === 1 ? "address" : "addresses"}, but there ${expectedCount === 1 ? "is" : "are"} ${expectedCount} ${expectedCount === 1 ? "inscription" : "inscriptions"}`);
  }
  return addresses;
}

export function applyDestinationAddresses<T extends { recipient?: string }>(items: T[], addresses: string[]): T[] {
  if (items.length !== addresses.length) {
    throw new Error(`Choose exactly one destination for each of the ${items.length} inscriptions`);
  }
  return items.map((item, index) => ({ ...item, recipient: addresses[index] }));
}
